import type {
  FairUseConfig,
  FairUseEntitlementField,
  FairUseEntitlementRow,
  FairUseEntitlements,
  FairUseEntitlementSchema,
  FairUseEntitlementSchemaField,
  FairUseReasoningEffort,
  FairUseTier,
  FairUseVoicePrices,
} from "@/api/admin/fair-use"

import {
  FAIR_USE_ENTITLEMENT_FIELDS,
  FAIR_USE_ENTITLEMENT_PLANS,
  FAIR_USE_MAX_VOICE_PRICE_SEK_PER_MINUTE,
} from "@/api/admin/fair-use"

import { parseFairUseSekLiteral } from "./fair-use-pricing-utils"

// Non-component helpers for the PLAN ENTITLEMENT matrix and the voice rate
// table (#3087, spec R1/R5) — Configuration → Fair use → Plans & pricing.
//
// Kept out of the .tsx files so those export components only (react-refresh),
// and out of `fair-use-utils.ts`, which is already well past the file-size
// limit. Nothing here imports from `fair-use-utils.ts`: the dependency runs the
// other way (the shared form state embeds the types defined here), so there is
// no cycle to reason about.
//
// Two rules drive the whole file:
//
//  1. `null` means "no limit" and is legal for FOUR fields only. Every other
//     integer is a real per-turn budget whose absence would mean an unbounded
//     turn, which is not a plan the platform sells — so a blank cell there is
//     an error, not a generous default.
//  2. Nothing is invented. When the backend sends no `entitlements` block (a
//     round-1 deployment), the matrix is not rendered at all rather than drawn
//     from the shipped defaults: a matrix stating limits this tenant is not on
//     is worse than no matrix.

// =============================================================================
// Per-field metadata
// =============================================================================

/** The editor a cell gets, and how its value is read back. */
export type FairUseEntitlementFieldType = "effort" | "boolean" | "integer" | "number"

export type FairUseEntitlementFieldMeta = {
  name: FairUseEntitlementField
  type: FairUseEntitlementFieldType
  /** Whether a blank cell is the legal "no limit" value. */
  nullable: boolean
  minimum: number
  /**
   * True when {@link minimum} is a floor the value must stay strictly ABOVE.
   * The SEK budget's 0 is such a floor — an unbounded-in-practice Deep run is
   * not a plan the platform sells, so the backend refuses it.
   */
  exclusiveMinimum: boolean
  /** `null` = no ceiling the client knows of. */
  maximum: number | null
  /** `effort` fields only: the ladder, weakest first. */
  options: FairUseReasoningEffort[]
}

/**
 * The reasoning-effort ladder, weakest first.
 *
 * Mirrors `fair_use/constants.py::VALID_REASONING_EFFORTS`, used only when the
 * backend sent no `entitlement_schema`; when it did, its `options` win, so a
 * new platform stop appears here without a frontend release.
 */
const FALLBACK_EFFORTS: FairUseReasoningEffort[] = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
]

/**
 * Field metadata as shipped, for a backend that sends no `entitlement_schema`.
 *
 * Mirrors `BOOL_ENTITLEMENT_FIELDS` / `INT_ENTITLEMENT_FIELDS` /
 * `NULLABLE_ENTITLEMENT_FIELDS` / `ZERO_ALLOWED_ENTITLEMENT_FIELDS` /
 * `ENTITLEMENT_FIELD_MAXIMUM`. Bounds only ever narrow what a save may carry,
 * so a stale copy costs a needless 400, never a silently accepted value.
 */
const FALLBACK_FIELD_META: Record<FairUseEntitlementField, FairUseEntitlementFieldMeta> = {
  max_reasoning_effort: {
    name: "max_reasoning_effort",
    type: "effort",
    nullable: false,
    minimum: 0,
    exclusiveMinimum: false,
    maximum: null,
    options: FALLBACK_EFFORTS,
  },
  deep: {
    name: "deep",
    type: "boolean",
    nullable: false,
    minimum: 0,
    exclusiveMinimum: false,
    maximum: null,
    options: [],
  },
  voice: {
    name: "voice",
    type: "boolean",
    nullable: false,
    minimum: 0,
    exclusiveMinimum: false,
    maximum: null,
    options: [],
  },
  scheduled_flows: {
    name: "scheduled_flows",
    type: "integer",
    nullable: true,
    minimum: 0,
    exclusiveMinimum: false,
    maximum: 1000,
    options: [],
  },
  flow_max_steps: {
    name: "flow_max_steps",
    type: "integer",
    nullable: true,
    minimum: 1,
    exclusiveMinimum: false,
    maximum: 10000,
    options: [],
  },
  flow_max_seconds: {
    name: "flow_max_seconds",
    type: "integer",
    nullable: true,
    minimum: 1,
    exclusiveMinimum: false,
    maximum: 86400,
    options: [],
  },
  images_per_month: {
    name: "images_per_month",
    type: "integer",
    nullable: true,
    minimum: 0,
    exclusiveMinimum: false,
    maximum: 100000,
    options: [],
  },
  max_tool_iterations: {
    name: "max_tool_iterations",
    type: "integer",
    nullable: false,
    minimum: 1,
    exclusiveMinimum: false,
    maximum: 200,
    options: [],
  },
  turn_max_runtime_seconds: {
    name: "turn_max_runtime_seconds",
    type: "integer",
    nullable: false,
    minimum: 1,
    exclusiveMinimum: false,
    maximum: 86400,
    options: [],
  },
  deep_token_budget: {
    name: "deep_token_budget",
    type: "integer",
    nullable: false,
    minimum: 1,
    exclusiveMinimum: false,
    maximum: 10000000,
    options: [],
  },
  deep_spend_budget_sek: {
    name: "deep_spend_budget_sek",
    type: "number",
    nullable: false,
    // 0 is a floor the value must stay ABOVE, not a legal amount: a 0 SEK Deep
    // budget would stop every Deep run on its first priced call.
    minimum: 0,
    exclusiveMinimum: true,
    maximum: 10000,
    options: [],
  },
  deep_recursion_limit: {
    name: "deep_recursion_limit",
    type: "integer",
    nullable: false,
    minimum: 1,
    exclusiveMinimum: false,
    maximum: 10000,
    options: [],
  },
  capacity_retry_budget: {
    name: "capacity_retry_budget",
    type: "integer",
    nullable: false,
    minimum: 0,
    exclusiveMinimum: false,
    maximum: 10,
    options: [],
  },
}

function isKnownField(name: string): name is FairUseEntitlementField {
  return (FAIR_USE_ENTITLEMENT_FIELDS as readonly string[]).includes(name)
}

function isEffort(value: string): value is FairUseReasoningEffort {
  return (FALLBACK_EFFORTS as string[]).includes(value)
}

function mergeMeta(
  base: FairUseEntitlementFieldMeta,
  wire: FairUseEntitlementSchemaField
): FairUseEntitlementFieldMeta {
  const options =
    base.type === "effort" && Array.isArray(wire.options)
      ? wire.options.filter(isEffort)
      : base.options
  return {
    name: base.name,
    // The wire type is advisory: the editor and the parser are chosen by the
    // TYPED cell this side keeps, so a `type` that disagrees is ignored rather
    // than used to render a Switch over an integer budget.
    type: base.type,
    nullable: typeof wire.nullable === "boolean" ? wire.nullable : base.nullable,
    minimum: typeof wire.minimum === "number" ? wire.minimum : base.minimum,
    exclusiveMinimum:
      typeof wire.exclusive_minimum === "boolean" ? wire.exclusive_minimum : base.exclusiveMinimum,
    maximum: typeof wire.maximum === "number" ? wire.maximum : base.maximum,
    options: options.length > 0 ? options : base.options,
  }
}

/**
 * The thirteen fields with their bounds, in canonical order.
 *
 * The ORDER and the SET come from this side ({@link FAIR_USE_ENTITLEMENT_FIELDS}) —
 * every one has a typed cell, an editor and a parser here. The schema supplies
 * bounds and the effort ladder for the fields both sides know; a field only the
 * backend knows is left to a later release rather than rendered blind.
 */
export function fairUseEntitlementFields(
  schema?: FairUseEntitlementSchema
): FairUseEntitlementFieldMeta[] {
  const wireByName = new Map<string, FairUseEntitlementSchemaField>()
  // Read through `Partial`, because this is wire data: the declared type says
  // every entry has a `name`, and the entry that does not is exactly the one
  // worth surviving.
  for (const raw of (schema?.fields ?? []) as Partial<FairUseEntitlementSchemaField>[]) {
    const name: unknown = raw.name
    if (typeof name !== "string" || !isKnownField(name)) continue
    wireByName.set(name, raw as FairUseEntitlementSchemaField)
  }
  return FAIR_USE_ENTITLEMENT_FIELDS.map((name) => {
    const base = FALLBACK_FIELD_META[name]
    const wire = wireByName.get(name)
    return wire ? mergeMeta(base, wire) : base
  })
}

/**
 * The matrix columns: the plans the platform assigns, ladder order.
 *
 * `custom` is never a column — the backend resolves a legacy per-user cap
 * override to the Standard row, so it has no entitlements of its own.
 */
export function fairUseEntitlementPlans(schema?: FairUseEntitlementSchema): FairUseTier[] {
  const wire = schema?.plans
  if (!Array.isArray(wire)) return [...FAIR_USE_ENTITLEMENT_PLANS]
  const known = wire.filter((plan): plan is FairUseTier =>
    (FAIR_USE_ENTITLEMENT_PLANS as readonly string[]).includes(plan)
  )
  return known.length > 0 ? known : [...FAIR_USE_ENTITLEMENT_PLANS]
}

// =============================================================================
// Form state
// =============================================================================

/**
 * One plan's row while it is being edited.
 *
 * Integers are strings for the same reason every other numeric field on this
 * surface is: a partially typed "1" / "" must not collapse to NaN mid-edit.
 * An EMPTY string is the "no limit" value for the four nullable fields and an
 * error for the rest.
 */
export type FairUseEntitlementFormRow = {
  max_reasoning_effort: FairUseReasoningEffort
  deep: boolean
  voice: boolean
  scheduled_flows: string
  flow_max_steps: string
  flow_max_seconds: string
  images_per_month: string
  max_tool_iterations: string
  turn_max_runtime_seconds: string
  deep_token_budget: string
  /** SEK, so a decimal cell: "5,89" and "5.89" both read as 5.89. */
  deep_spend_budget_sek: string
  deep_recursion_limit: string
  capacity_retry_budget: string
}

export type FairUseEntitlementsFormState = Record<FairUseTier, FairUseEntitlementFormRow>

/** Every cell that is typed into rather than switched or selected. */
export type FairUseEntitlementNumericField = Exclude<
  FairUseEntitlementField,
  "max_reasoning_effort" | "deep" | "voice"
>

/** The WHOLE-number cells — every numeric field but the SEK budget. */
export type FairUseEntitlementIntegerField = Exclude<
  FairUseEntitlementNumericField,
  "deep_spend_budget_sek"
>

const INTEGER_FIELDS: FairUseEntitlementIntegerField[] = [
  "scheduled_flows",
  "flow_max_steps",
  "flow_max_seconds",
  "images_per_month",
  "max_tool_iterations",
  "turn_max_runtime_seconds",
  "deep_token_budget",
  "deep_recursion_limit",
  "capacity_retry_budget",
]

/**
 * The DECIMAL cells (#3089, spec R3).
 *
 * One field today, kept as a list rather than a special case so a second money
 * entitlement does not have to rediscover which parser it needs.
 */
const DECIMAL_FIELDS: "deep_spend_budget_sek"[] = ["deep_spend_budget_sek"]

const NUMERIC_FIELDS: FairUseEntitlementNumericField[] = [...INTEGER_FIELDS, ...DECIMAL_FIELDS]

function numericCell(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : ""
}

function rowToForm(row: Partial<FairUseEntitlementRow> | undefined): FairUseEntitlementFormRow {
  const effort =
    typeof row?.max_reasoning_effort === "string" && isEffort(row.max_reasoning_effort)
      ? row.max_reasoning_effort
      : "medium"
  const form = {
    max_reasoning_effort: effort,
    deep: row?.deep === true,
    voice: row?.voice === true,
  } as FairUseEntitlementFormRow
  for (const field of NUMERIC_FIELDS) {
    form[field] = numericCell(row?.[field])
  }
  return form
}

/** The stored matrix as editable strings. Missing plans read as an empty row. */
export function fairUseEntitlementsToFormState(
  entitlements: FairUseEntitlements | undefined
): FairUseEntitlementsFormState {
  return {
    standard: rowToForm(entitlements?.standard),
    pro: rowToForm(entitlements?.pro),
    unlimited: rowToForm(entitlements?.unlimited),
  }
}

/** Whether this config carries a round-2 entitlement block at all. */
export function fairUseHasEntitlements(config: Pick<FairUseConfig, "entitlements">): boolean {
  const entitlements = config.entitlements
  return !!entitlements && typeof entitlements === "object"
}

// =============================================================================
// Parsing and validation
// =============================================================================

/** Digits only — no separators, no sign, no exponent, no `Number` coercions. */
const INTEGER_LITERAL = /^\d+$/

/**
 * One integer cell as a number, `null` for the legal blank, or `undefined`
 * when it is unusable.
 *
 * Three answers rather than two on purpose: `null` ("no limit") and "this cell
 * is wrong" are different outcomes, and collapsing them would let a typo on
 * `deep_token_budget` save as an unbounded Deep turn.
 */
export function parseFairUseEntitlementInteger(
  value: string,
  meta: FairUseEntitlementFieldMeta
): number | null | undefined {
  const trimmed = value.trim()
  if (trimmed === "") return meta.nullable ? null : undefined
  if (!INTEGER_LITERAL.test(trimmed)) return undefined
  const parsed = Number(trimmed)
  if (!Number.isSafeInteger(parsed)) return undefined
  if (parsed < meta.minimum) return undefined
  if (meta.maximum !== null && parsed > meta.maximum) return undefined
  return parsed
}

/**
 * One DECIMAL cell as a number, or `undefined` when it is unusable.
 *
 * A sibling of {@link parseFairUseEntitlementInteger} rather than a widening of
 * it: that parser refuses "5,89" on purpose, because a decimal in a token or
 * iteration budget is a typo. Money is the opposite — 5.89 SEK is the value,
 * and rounding it to 5 or 6 moves the trip point by up to 9 %.
 *
 * Never `null`: no money entitlement is nullable, so "empty" here is an error
 * rather than "no limit". The separator rule is the surface's own — a comma or
 * a point, no thousands grouping — shared with every other SEK box on this
 * config through {@link parseFairUseSekLiteral}.
 */
export function parseFairUseEntitlementDecimal(
  value: string,
  meta: FairUseEntitlementFieldMeta
): number | undefined {
  const parsed = parseFairUseSekLiteral(value)
  if (parsed == null) return undefined
  if (meta.exclusiveMinimum ? parsed <= meta.minimum : parsed < meta.minimum) return undefined
  if (meta.maximum !== null && parsed > meta.maximum) return undefined
  return parsed
}

/**
 * One numeric cell, whichever parser its field calls for.
 *
 * `undefined` is always "fix this box" and `null` always "no limit", so the
 * matrix and the save path never have to know which of the two parsers ran.
 */
export function parseFairUseEntitlementCell(
  value: string,
  meta: FairUseEntitlementFieldMeta
): number | null | undefined {
  return meta.type === "number"
    ? parseFairUseEntitlementDecimal(value, meta)
    : parseFairUseEntitlementInteger(value, meta)
}

/** One cell the admin has to fix before the panel can save. */
export type FairUseEntitlementCellError = {
  plan: FairUseTier
  field: FairUseEntitlementNumericField
}

/**
 * Every unusable cell in the matrix, or `[]` when all of them parse.
 *
 * Cell-level rather than a single "something is wrong": the matrix is 36 boxes
 * and one sentence at the bottom would leave the admin hunting for which one.
 */
export function fairUseEntitlementIssues(
  form: FairUseEntitlementsFormState,
  schema?: FairUseEntitlementSchema
): FairUseEntitlementCellError[] {
  const metaByName = new Map(fairUseEntitlementFields(schema).map((meta) => [meta.name, meta]))
  const errors: FairUseEntitlementCellError[] = []
  for (const plan of fairUseEntitlementPlans(schema)) {
    for (const field of NUMERIC_FIELDS) {
      const meta = metaByName.get(field)
      if (!meta) continue
      if (parseFairUseEntitlementCell(form[plan][field], meta) === undefined) {
        errors.push({ plan, field })
      }
    }
  }
  return errors
}

/**
 * The matrix as a wire payload, or `null` when any cell is unusable.
 *
 * The COMPLETE block is sent, not a diff: the backend merges what it is given
 * onto the stored matrix and persists all three plans, and a complete payload
 * is also what makes the post-write read-back check
 * ({@link fairUseEntitlementsApplied}) a statement about every cell rather than
 * about the one that happened to change.
 */
export function fairUseFormStateToEntitlements(
  form: FairUseEntitlementsFormState,
  schema?: FairUseEntitlementSchema
): FairUseEntitlements | null {
  const metaByName = new Map(fairUseEntitlementFields(schema).map((meta) => [meta.name, meta]))
  const plans = fairUseEntitlementPlans(schema)
  const entitlements = {} as FairUseEntitlements
  for (const plan of plans) {
    const source = form[plan]
    // The four nullable fields are the only ones a `null` can reach: the parser
    // already refused a blank anywhere else, which is what makes the cast at
    // the end of the row a statement the parser has proved rather than a hope.
    // `deep_spend_budget_sek` rides the same loop with the decimal parser, so
    // the SEK cell cannot be forgotten by a later field being added above it.
    const integers: Record<string, number | null> = {}
    for (const field of NUMERIC_FIELDS) {
      const meta = metaByName.get(field)
      if (!meta) return null
      const parsed = parseFairUseEntitlementCell(source[field], meta)
      if (parsed === undefined) return null
      integers[field] = parsed
    }
    entitlements[plan] = {
      max_reasoning_effort: source.max_reasoning_effort,
      deep: source.deep,
      voice: source.voice,
      ...integers,
    } as FairUseEntitlementRow
  }
  return entitlements
}

/**
 * Whether the platform stored the matrix that was sent.
 *
 * Structural, not `===`: `fairUseUnappliedConfigFields` compares by identity,
 * and two objects off two round trips are never the same reference — an
 * identity check would report EVERY save as discarded. Compared per plan and
 * per field so a single dropped cell is caught, which is the failure the
 * read-back exists for.
 */
export function fairUseEntitlementsApplied(
  sent: FairUseEntitlements,
  // `Partial` on purpose: a plan the platform dropped from the answer is the
  // failure this check exists to catch, and the declared type cannot say so.
  saved: Partial<FairUseEntitlements> | undefined
): boolean {
  if (!saved) return false
  return (Object.keys(sent) as FairUseTier[]).every((plan) => {
    const sentRow = sent[plan]
    const savedRow = saved[plan]
    if (!savedRow) return false
    return FAIR_USE_ENTITLEMENT_FIELDS.every(
      (field) => (sentRow[field] ?? null) === (savedRow[field] ?? null)
    )
  })
}

// =============================================================================
// Voice rates
// =============================================================================

/**
 * One editable voice rate.
 *
 * `key` is a client-side row identity so React keeps the input mounted while
 * the admin retypes the model id — keying on the id itself would remount (and
 * blur) the field on every keystroke.
 */
export type FairUseVoicePriceRow = {
  key: string
  id: string
  rate: string
}

let voiceRowCounter = 0

export function fairUseVoicePriceRow(id = "", rate = ""): FairUseVoicePriceRow {
  voiceRowCounter += 1
  return { key: `voice-rate-${voiceRowCounter}`, id, rate }
}

/** The stored rate map as editable rows, `default` first and the rest sorted. */
export function fairUseVoicePricesToFormState(
  prices: FairUseVoicePrices | undefined
): FairUseVoicePriceRow[] {
  const entries = Object.entries(prices ?? {})
  entries.sort(([left], [right]) => {
    if (left === right) return 0
    if (left === "default") return -1
    if (right === "default") return 1
    return left < right ? -1 : 1
  })
  return entries.map(([id, rate]) => fairUseVoicePriceRow(id, String(rate)))
}

export type FairUseVoicePricesIssue = "id" | "duplicate" | "range" | null

/** A plain decimal, comma or point, no grouping — the surface's own rule. */
const VOICE_RATE_LITERAL = /^\d+([.,]\d+)?$/

/** One rate as a number, or `null` when it is unusable. */
export function parseFairUseVoiceRate(value: string): number | null {
  const trimmed = value.trim()
  if (!VOICE_RATE_LITERAL.test(trimmed)) return null
  const parsed = Number(trimmed.replace(",", "."))
  if (!Number.isFinite(parsed) || parsed < 0) return null
  if (parsed > FAIR_USE_MAX_VOICE_PRICE_SEK_PER_MINUTE) return null
  return parsed
}

/**
 * What is wrong with the rate table, if anything.
 *
 * Ids are compared LOWER-CASED because the backend lower-cases them on write
 * and refuses a case-insensitive duplicate with a 400: two rows differing only
 * in case would otherwise be one rate with a silent winner.
 */
export function fairUseVoicePricesIssue(rows: FairUseVoicePriceRow[]): FairUseVoicePricesIssue {
  const seen = new Set<string>()
  for (const row of rows) {
    const id = row.id.trim().toLowerCase()
    if (!id) return "id"
    if (seen.has(id)) return "duplicate"
    seen.add(id)
    if (parseFairUseVoiceRate(row.rate) === null) return "range"
  }
  return null
}

/** The rate table as a wire payload, or `null` when a row is unusable. */
export function fairUseFormStateToVoicePrices(
  rows: FairUseVoicePriceRow[]
): FairUseVoicePrices | null {
  if (fairUseVoicePricesIssue(rows) !== null) return null
  const prices: FairUseVoicePrices = {}
  for (const row of rows) {
    prices[row.id.trim().toLowerCase()] = parseFairUseVoiceRate(row.rate) as number
  }
  return prices
}

/** Whether the platform stored the rate map that was sent (see the matrix twin). */
export function fairUseVoicePricesApplied(
  sent: FairUseVoicePrices,
  saved: FairUseVoicePrices | undefined
): boolean {
  if (!saved) return false
  const sentIds = Object.keys(sent)
  if (sentIds.length !== Object.keys(saved).length) return false
  return sentIds.every((id) => saved[id] === sent[id])
}

/** The DOM id of one matrix cell, so the panel and its tests agree on one name. */
export function fairUseEntitlementCellId(plan: FairUseTier, field: string): string {
  return `fair-use-entitlement-${plan}-${field.replace(/_/g, "-")}`
}
